import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  checkRegistration,
  createRegistration,
  GetSecret,
  pingTest,
} from '@/api/mTlsAuthenticationApi';
import { MTlsRegistrationResponse } from '@/models/models';
import { DEVICE_REGISTRATION_FLAG_KEY, MTLS_SELECTED_BORER_ID_KEY } from '@/utilities/constants';
import { GenericLoginError, MinesightError, MTlsLoginError } from '@/utilities/minesightErrors';

import { useMst } from '../../../mobx-models/Root';
import { useConstructor } from '../../../utilities';
import LoginArea from './LoginArea';
import NewPasswordArea from './NewPasswordArea';

enum LoginViews {
  LOGIN,
  NEW_PASSWORD,
}

interface Props {
  loginHandler: (history: any) => Promise<void>;
}

const Login: React.FC<Props> = ({ loginHandler }: Props) => {
  const history = useHistory();
  const [view, setViewType] = useState<LoginViews>(LoginViews.LOGIN);
  const [userData, setUserData] = useState<any>();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<MinesightError | undefined>();
  const [useMTlsLogin, setUseMTlsLogin] = useState(true);
  const [registerIPad, setRegisterIPad] = useState(true);

  const { user } = useMst();

  const checkForSession = async () => {
    try {
      const userInfo = await Auth.currentUserInfo();
      if (!userInfo) return;
      await loginHandler(history);

      Sentry.setUser({ email: userInfo.attributes.email });
    } catch (error) {
      setLoginError(error);
      console.log('🚀 ~ file: Login.tsx ~ line 36 ~ checkForSession ~ error', error);
    }
  };

  useConstructor(() => {
    checkForSession();
  });

  const performPingTest = async () => {
    await pingTest();
  };

  useEffect(() => {
    try {
      performPingTest().then(() => {});
    } catch (error) {
      // do nothing
    }
  }, []);

  // handle expired certs error
  const handleExpiredCert = async (response: MTlsRegistrationResponse) => {
    try {
      if (response.isExpired) {
        await createRegistration(response.borerId);
      }
    } catch (e) {
      // do nothing - we don't want this to prevent the log in
    }
  };

  const handleErrorMessage = (error: any): MinesightError => {
    if (error.code) {
      return error;
    }

    return GenericLoginError;
  };

  const handleLogin = async (email: string, password: string) => {
    setIsLoggingIn(true);
    try {
      let awsUser;
      let lowerCaseEmail;
      if (useMTlsLogin) {
        const response = await checkRegistration();
        if (response && response.isRegistered) {
          awsUser = await Auth.signIn(response.email);
          if (awsUser.challengeName === 'CUSTOM_CHALLENGE') {
            const challengeResponse = await GetSecret();
            awsUser = await Auth.sendCustomChallengeAnswer(
              awsUser,
              challengeResponse.secretLoginKey,
            );
            if (awsUser) {
              await handleExpiredCert(response);
              localStorage.setItem(MTLS_SELECTED_BORER_ID_KEY, response.borerId);
            }
          }
        } else {
          throw MTlsLoginError;
        }
      } else {
        localStorage.setItem(DEVICE_REGISTRATION_FLAG_KEY, JSON.stringify(registerIPad));

        lowerCaseEmail = email.toLowerCase();
        awsUser = await Auth.signIn(lowerCaseEmail, password);
      }

      if (awsUser) {
        if (awsUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setUserData(awsUser);
          setViewType(LoginViews.NEW_PASSWORD);
          return;
        }
        // Gets and assigns siteId to User Model
        await user.getProfile();

        Sentry.setUser({ email: lowerCaseEmail });
        await loginHandler(history);
      }
    } catch (error) {
      console.log('🚀 ~ file: Login.tsx:65 ~ handleLogin ~ error:', error);
      setLoginError(handleErrorMessage(error));
      setUseMTlsLogin(false);
      await Auth.signOut();
    }
    setIsLoggingIn(false);
  };

  const saveNewPassword = async (newPassword: string) => {
    try {
      await Auth.completeNewPassword(userData, newPassword);

      history.push('/home');
    } catch (error) {
      console.log('🚀 ~ file: Login.tsx ~ line 85 ~ saveNewPassword ~ error', error);
    }
  };

  const clearError = () => {
    setLoginError(undefined);
  };

  const getView = () => {
    switch (view) {
      case LoginViews.NEW_PASSWORD:
        return <NewPasswordArea saveNewPassword={saveNewPassword} />;
      case LoginViews.LOGIN:
      default:
        return (
          <LoginArea
            loginUser={handleLogin}
            isLoggingIn={isLoggingIn}
            error={loginError}
            clearError={clearError}
            useMTlsLogin={useMTlsLogin}
            setUseMTlsLogin={setUseMTlsLogin}
            registerIPad={registerIPad}
            setRegisterIPad={setRegisterIPad}
          />
        );
    }
  };

  return <>{getView()}</>;
};

export default Login;
